import type { Map as MapboxMap } from "mapbox-gl";
import type { AssignmentsResponse, GeoJsonFeature, AssignmentFeatureProperties } from "../../api/map/types";
import { applyIncrementalUpdate, setCachedFeatures } from "./utils";

const SOURCE_ID = "assignments";

/**
 * Ensures the assignments layer exists on the map (idempotent)
 */
export const ensureAssignmentsLayer = (map: MapboxMap): void => {
    if (!map.getSource(SOURCE_ID)) {
        try {
            map.addSource(SOURCE_ID, {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] }
            });
        } catch {
            return;
        }
    }

    // Insert assignment layers below address circles so address points stay clickable
    // and are visually on top of assignment fill areas.
    const beforeId = map.getLayer("addresses-points") ? "addresses-points" : undefined;

    if (!map.getLayer(`${SOURCE_ID}-fill`)) {
        try {
            map.addLayer({
                id: `${SOURCE_ID}-fill`,
                type: "fill",
                source: SOURCE_ID,
                paint: {
                    "fill-color": [
                        "interpolate",
                        ["linear"],
                        ["get", "progressPercent"],
                        0, "#f97316",
                        0.25, "#fb923c",
                        0.5, "#facc15",
                        0.75, "#84cc16",
                        1, "#22c55e"
                    ],
                    "fill-opacity": [
                        "match",
                        ["get", "status"],
                        "Draft", 0.2,
                        "Planned", 0.3,
                        "InProgress", 0.4,
                        "Completed", 0.35,
                        0.3
                    ]
                }
            }, beforeId);
        } catch (e) {
            console.warn("[AssignmentsLayer] Could not add fill layer:", e);
        }
    }

    if (!map.getLayer(`${SOURCE_ID}-outline`)) {
        try {
            map.addLayer({
                id: `${SOURCE_ID}-outline`,
                type: "line",
                source: SOURCE_ID,
                paint: {
                    "line-color": [
                        "match",
                        ["get", "status"],
                        "Draft", "#94a3b8",
                        "Planned", "#3b82f6",
                        "InProgress", "#f59e0b",
                        "Completed", "#22c55e",
                        "Cancelled", "#ef4444",
                        "#3b82f6"
                    ],
                    "line-width": [
                        "interpolate", ["linear"], ["zoom"],
                        10, 1,
                        15, 2,
                        18, 3
                    ],
                    "line-opacity": [
                        "match", ["get", "status"],
                        "Draft", 0.5,
                        1
                    ]
                }
            }, beforeId);
        } catch (e) {
            console.warn("[AssignmentsLayer] Could not add outline layer:", e);
        }
    }

    if (!map.getLayer(`${SOURCE_ID}-labels`)) {
        try {
            map.addLayer({
                id: `${SOURCE_ID}-labels`,
                type: "symbol",
                source: SOURCE_ID,
                layout: {
                    "text-field": ["get", "name"],
                    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                    "text-size": 12,
                    "text-anchor": "center",
                    "text-allow-overlap": false
                },
                paint: {
                    "text-color": "#1f2937",
                    "text-halo-color": "#ffffff",
                    "text-halo-width": 2
                }
            }, beforeId);
        } catch (e) {
            console.warn("[AssignmentsLayer] Could not add labels layer:", e);
        }
    }
};

/**
 * Updates the assignments layer with data from the API
 * Uses incremental updates when possible to minimize re-rendering
 */
export const updateAssignmentsLayer = (
    map: MapboxMap,
    data: AssignmentsResponse | null,
    useIncremental: boolean = true
): void => {
    // Ensure source/layers exist before trying to update
    ensureAssignmentsLayer(map);

    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    if (!data || data.features.length === 0) {
        geojsonSource.setData({ type: "FeatureCollection", features: [] });
        return;
    }

    // Convert API response to Mapbox GeoJSON format
    const newFeatures: GeoJsonFeature<AssignmentFeatureProperties & { progressPercent: number }>[] = data.features
        .filter((feature) => {
            const gtype = feature.geometry?.type;
            return gtype === "Polygon" || gtype === "MultiPolygon";
        })
        .map((feature) => ({
            type: "Feature" as const,
            id: feature.id ?? feature.properties.assignmentId,
            geometry: feature.geometry,
            properties: {
                ...feature.properties,
                // Ensure progressPercent is a number (0-1) for styling
                progressPercent:
                    feature.properties.progressPercent !== null &&
                        feature.properties.progressPercent !== undefined
                        ? feature.properties.progressPercent / 100
                        : 0,
                assignmentId: feature.properties.assignmentId,
                status: feature.properties.status
            }
        }));

    if (newFeatures.length === 0) {
        geojsonSource.setData({ type: "FeatureCollection", features: [] });
        return;
    }

    // Use incremental update if enabled
    if (useIncremental) {
        try {
            applyIncrementalUpdate(
                map,
                geojsonSource,
                SOURCE_ID,
                newFeatures,
                (feature) => feature.id ?? feature.properties.assignmentId
            );
            return;
        } catch (error) {
            // Fallback to full update on error
            console.warn("Incremental update failed, falling back to full update:", error);
        }
    }

    // Full update (incremental disabled or error)
    const geojson = {
        type: "FeatureCollection" as const,
        features: newFeatures
    };

    geojsonSource.setData(geojson as any);
    setCachedFeatures(map, SOURCE_ID, newFeatures);
};